// How the backoffice lets the user pick the param of each deeplink base. Shared by the
// notification form (DeepLink.vue) and the practical info buttons (Buttons.vue).
import { type DeeplinkTarget } from '../../pages/notifications/notifications.service'

/**
 * How the UI should let the user pick a param value for a base.
 *
 * - `entity-picker` → render a `<v-autocomplete>` populated with entities
 * - `text-input`    → render a `<v-text-field>` for a URL or other free text
 * - `no-param`      → render a simple label ("no parameter required")
 */
export type TargetCategory = 'entity-picker' | 'text-input' | 'no-param'

/**
 * Which entity list the UI should render for picking a value.
 * The actual list is fetched and provided by `useDeeplinkEntities()`.
 */
export type EntitySource =
  | 'showmen'
  | 'maps'
  | 'programs'
  | 'shows'
  | 'infos'
  | 'gallery'
  | 'guestTypes'

// Exhaustive over DeeplinkTarget: adding a base without its entry is a compile error.
const TARGET_META = {
  home: { category: 'no-param' },
  showMan: { category: 'entity-picker', entitySource: 'showmen' },
  show: { category: 'entity-picker', entitySource: 'shows' },
  program: {
    category: 'entity-picker',
    entitySource: 'programs',
    multiselect: true,
  },
  sponsors: { category: 'no-param' },
  practicalInfo: { category: 'entity-picker', entitySource: 'infos' },
  map: { category: 'entity-picker', entitySource: 'maps', multiselect: true },
  notifications: { category: 'no-param' },
  webview: { category: 'text-input' },
  extern: { category: 'text-input' },
  gallery: {
    category: 'entity-picker',
    entitySource: 'gallery',
    multiselect: true,
  },
  showmen: {
    category: 'entity-picker',
    entitySource: 'guestTypes',
    multiselect: true,
  },
  practicalInfos: {
    category: 'entity-picker',
    entitySource: 'infos',
    multiselect: true,
  },
  chat: { category: 'no-param' },
  likes: { category: 'no-param' },
  myTickets: { category: 'no-param' },
  weezevent: { category: 'text-input' },
  tickets: { category: 'text-input' },
  cashless: { category: 'text-input' },
} as const satisfies Record<
  DeeplinkTarget,
  {
    category: TargetCategory
    entitySource?: EntitySource
    multiselect?: boolean
  }
>

/** Returns the UI rendering category for a given base name. */
export function getTargetCategory(base: DeeplinkTarget): TargetCategory {
  const meta = TARGET_META[base]
  return meta.category ?? 'no-param'
}

/** True when the base stores multiple entity IDs (comma-separated in the editor). */
export function isMultiSelect(base: DeeplinkTarget): boolean {
  const meta = TARGET_META[base]
  return meta && 'multiselect' in meta ? meta.multiselect : false
}

/** Returns the entity source key for a base, or undefined if the base has no entity list. */
export function getEntitySource(
  base: DeeplinkTarget,
): EntitySource | undefined {
  const meta = TARGET_META[base]
  return meta && 'entitySource' in meta ? meta.entitySource : undefined
}
